/**
 * Escalation Engine
 * Движок обработки эскалаций
 *
 * Enhanced with:
 * - Max escalation depth limits
 * - Ticket state conditions
 * - Cooling period between escalations
 * - Escalation chain tracking
 */

import type {
  EscalationEvent,
  EscalationRequest,
  EscalationPolicy,
  EscalationNotification,
  EscalationMetrics,
  EscalationValidationResult,
  EscalationChainInfo,
  TicketState
} from './types'
import { EscalationStatus, DEFAULT_ESCALATION_POLICY_VALUES } from './types'
import type { EscalationRule } from '../../sla'
import { EscalationAction, EscalationRole } from '../../sla'

/**
 * Process escalation for a ticket
 */
export async function processEscalation(
  request: EscalationRequest,
  rule: EscalationRule,
  policy?: EscalationPolicy,
  previousEscalations?: EscalationEvent[]
): Promise<EscalationEvent> {
  // Validate escalation can proceed
  const validation = validateEscalation(request, rule, policy, previousEscalations || [])
  if (!validation.canEscalate) {
    // Return a failed event with the reason
    return {
      id: generateEscalationId(),
      ticketId: request.ticketId,
      ticketNumber: request.ticketNumber,
      slaConfigId: request.slaConfigId,
      ruleId: request.ruleId,
      triggeredAt: Date.now(),
      hoursElapsed: request.hoursElapsed,
      notifyRole: rule.notifyRole,
      notifiedUserIds: rule.notifyUserIds,
      action: rule.action,
      notificationsSent: [],
      wasReassigned: false,
      escalationDepth: request.currentEscalationDepth,
      ticketStateAtTrigger: request.ticketState,
      createdAt: Date.now(),
      status: EscalationStatus.FAILED,
      errorMessage: `Escalation blocked: ${validation.blockReason}${validation.details ? ` - ${validation.details}` : ''}`
    }
  }

  const newDepth = request.currentEscalationDepth + 1
  const event: EscalationEvent = {
    id: generateEscalationId(),
    ticketId: request.ticketId,
    ticketNumber: request.ticketNumber,
    slaConfigId: request.slaConfigId,
    ruleId: request.ruleId,
    triggeredAt: Date.now(),
    hoursElapsed: request.hoursElapsed,
    notifyRole: rule.notifyRole,
    notifiedUserIds: rule.notifyUserIds,
    action: rule.action,
    notificationsSent: [],
    wasReassigned: false,
    escalationDepth: newDepth,
    parentEscalationId: request.parentEscalationId,
    ticketStateAtTrigger: request.ticketState,
    createdAt: Date.now(),
    status: EscalationStatus.PENDING
  }

  try {
    event.status = EscalationStatus.PROCESSING

    // Send notifications
    const notifications = await sendEscalationNotifications(
      event,
      policy
    )
    event.notificationsSent = notifications

    // Execute action
    switch (rule.action) {
      case EscalationAction.NOTIFY:
        // Just notifications, already sent
        break

      case EscalationAction.REASSIGN:
        if (rule.autoReassign) {
          event.wasReassigned = true
          event.reassignedToUserId = rule.reassignToUserId
          event.reassignedToQueueId = rule.reassignToQueueId
        }
        break

      case EscalationAction.ESCALATE:
        // Increase priority
        event.priorityChanged = true
        event.newPriority = increasePriority(request.currentPriority)
        break
    }

    event.status = EscalationStatus.COMPLETED
    event.processedAt = Date.now()
  } catch (error) {
    event.status = EscalationStatus.FAILED
    event.errorMessage = error instanceof Error ? error.message : 'Escalation processing failed'
  }

  return event
}

/**
 * Send escalation notifications
 */
async function sendEscalationNotifications(
  event: EscalationEvent,
  policy?: EscalationPolicy
): Promise<EscalationNotification[]> {
  const notifications: EscalationNotification[] = []

  // Determine notification methods
  const methods = policy?.notificationMethods || ['system']

  for (const userId of event.notifiedUserIds) {
    for (const method of methods) {
      const notification: EscalationNotification = {
        userId,
        userName: userId,
        method,
        sentAt: Date.now(),
        delivered: false
      }

      try {
        // Send notification via appropriate channel
        await sendNotification(notification, event)
        notification.delivered = true
      } catch (error) {
        notification.delivered = false
        notification.errorMessage =
          error instanceof Error ? error.message : 'Failed to send'
      }

      notifications.push(notification)
    }
  }

  return notifications
}

/**
 * Send individual notification
 */
async function sendNotification(
  _notification: EscalationNotification,
  _event: EscalationEvent
): Promise<void> {
  throw new Error('Escalation notification transport is not integrated with a backend service.')
}

/**
 * Validate if escalation can proceed
 * Checks max depth, cooling period, and state conditions
 */
export function validateEscalation(
  request: EscalationRequest,
  rule: EscalationRule,
  policy: EscalationPolicy | undefined,
  previousEscalations: EscalationEvent[]
): EscalationValidationResult {
  // Get effective limits (use policy values or defaults)
  const maxDepth = policy?.maxEscalationDepth ?? DEFAULT_ESCALATION_POLICY_VALUES.maxEscalationDepth
  const coolingPeriod = policy?.coolingPeriodMinutes ?? DEFAULT_ESCALATION_POLICY_VALUES.coolingPeriodMinutes
  const allowedStates = policy?.allowedStates ?? DEFAULT_ESCALATION_POLICY_VALUES.allowedStates
  const blockedStates = policy?.blockedStates ?? DEFAULT_ESCALATION_POLICY_VALUES.blockedStates
  const pauseOnStates = policy?.pauseOnStates ?? DEFAULT_ESCALATION_POLICY_VALUES.pauseOnStates

  // Check if policy is active
  if (policy && !policy.isActive) {
    return {
      canEscalate: false,
      blockReason: 'policy_inactive',
      details: 'Escalation policy is disabled'
    }
  }

  // Check max depth
  if (request.currentEscalationDepth >= maxDepth) {
    return {
      canEscalate: false,
      blockReason: 'max_depth_exceeded',
      details: `Current depth ${request.currentEscalationDepth} exceeds maximum ${maxDepth}`,
      currentDepth: request.currentEscalationDepth,
      maxDepth
    }
  }

  // Check cooling period
  if (request.lastEscalationAt) {
    const coolingMs = coolingPeriod * 60 * 1000
    const timeSinceLastEscalation = Date.now() - request.lastEscalationAt
    if (timeSinceLastEscalation < coolingMs) {
      const remainingMinutes = Math.ceil((coolingMs - timeSinceLastEscalation) / 60000)
      return {
        canEscalate: false,
        blockReason: 'cooling_period_active',
        details: `Cooling period not expired. ${remainingMinutes} minutes remaining.`,
        suggestedWaitTime: remainingMinutes
      }
    }
  }

  // Check blocked states first (higher priority than allowed)
  if (blockedStates.includes(request.ticketState)) {
    return {
      canEscalate: false,
      blockReason: 'state_blocked',
      details: `Ticket state '${request.ticketState}' is blocked from escalation`
    }
  }

  // Check pause states (SLA paused)
  if (pauseOnStates.includes(request.ticketState)) {
    return {
      canEscalate: false,
      blockReason: 'sla_paused',
      details: `SLA timer paused because ticket is in '${request.ticketState}' state`
    }
  }

  // Check allowed states
  if (!allowedStates.includes(request.ticketState)) {
    return {
      canEscalate: false,
      blockReason: 'state_not_allowed',
      details: `Ticket state '${request.ticketState}' is not in the allowed states list`
    }
  }

  // Check time threshold
  if (request.hoursElapsed < rule.afterHours) {
    return {
      canEscalate: false,
      blockReason: 'time_not_reached',
      details: `Only ${request.hoursElapsed.toFixed(1)} hours elapsed, need ${rule.afterHours} hours`
    }
  }

  // Check if already triggered for this rule
  const alreadyTriggered = previousEscalations.some(
    (event) =>
      event.ruleId === rule.id &&
      event.status === EscalationStatus.COMPLETED &&
      event.ticketId === request.ticketId
  )
  if (alreadyTriggered) {
    return {
      canEscalate: false,
      blockReason: 'already_triggered',
      details: `Rule '${rule.id}' already triggered for this ticket`
    }
  }

  return {
    canEscalate: true,
    currentDepth: request.currentEscalationDepth,
    maxDepth
  }
}

/**
 * Get escalation chain info for a ticket
 */
export function getEscalationChainInfo(
  ticketId: string,
  events: EscalationEvent[],
  policy?: EscalationPolicy
): EscalationChainInfo {
  const ticketEvents = events
    .filter((e) => e.ticketId === ticketId && e.status === EscalationStatus.COMPLETED)
    .sort((a, b) => a.triggeredAt - b.triggeredAt)

  const maxDepth = policy?.maxEscalationDepth ?? DEFAULT_ESCALATION_POLICY_VALUES.maxEscalationDepth
  const coolingPeriod = policy?.coolingPeriodMinutes ?? DEFAULT_ESCALATION_POLICY_VALUES.coolingPeriodMinutes
  const currentDepth = ticketEvents.length

  const chain = ticketEvents.map((event, index) => ({
    escalationId: event.id,
    level: index + 1,
    triggeredAt: event.triggeredAt,
    notifyRole: event.notifyRole
  }))

  const lastEscalation = ticketEvents[ticketEvents.length - 1]
  let nextAvailableAt: number | undefined
  let canEscalate = currentDepth < maxDepth

  if (lastEscalation) {
    const coolingMs = coolingPeriod * 60 * 1000
    const nextTime = lastEscalation.triggeredAt + coolingMs
    if (Date.now() < nextTime) {
      canEscalate = false
      nextAvailableAt = nextTime
    }
  }

  return {
    ticketId,
    currentDepth,
    maxDepth,
    isAtMaxDepth: currentDepth >= maxDepth,
    chain,
    canEscalate,
    nextAvailableAt
  }
}

/**
 * Check if ticket state allows SLA timer to run
 */
export function isSlaTimerActive(
  ticketState: TicketState,
  policy?: EscalationPolicy
): boolean {
  const pauseOnStates = policy?.pauseOnStates ?? DEFAULT_ESCALATION_POLICY_VALUES.pauseOnStates
  const blockedStates = policy?.blockedStates ?? DEFAULT_ESCALATION_POLICY_VALUES.blockedStates

  return !pauseOnStates.includes(ticketState) && !blockedStates.includes(ticketState)
}

/**
 * Calculate adjusted elapsed time considering paused states
 */
export function calculateAdjustedElapsedTime(
  stateHistory: Array<{ state: TicketState; enteredAt: number; exitedAt?: number }>,
  policy?: EscalationPolicy
): number {
  const pauseOnStates = policy?.pauseOnStates ?? DEFAULT_ESCALATION_POLICY_VALUES.pauseOnStates
  const now = Date.now()
  let totalPausedMs = 0

  for (const entry of stateHistory) {
    if (pauseOnStates.includes(entry.state)) {
      const exitTime = entry.exitedAt ?? now
      totalPausedMs += exitTime - entry.enteredAt
    }
  }

  const startTime = stateHistory[0]?.enteredAt ?? now
  const totalElapsedMs = now - startTime
  const activeTimeMs = totalElapsedMs - totalPausedMs

  return activeTimeMs / (1000 * 60 * 60) // Convert to hours
}

/**
 * Increase ticket priority
 */
function increasePriority(
  currentPriority: 'high' | 'mid' | 'low'
): 'high' | 'mid' | 'low' {
  switch (currentPriority) {
    case 'low':
      return 'mid'
    case 'mid':
      return 'high'
    case 'high':
      return 'high' // Already at max
    default:
      return 'mid'
  }
}

/**
 * Check if escalation should be triggered
 */
export function shouldTriggerEscalation(
  hoursElapsed: number,
  rule: EscalationRule,
  previousEscalations: EscalationEvent[]
): boolean {
  // Check if we've reached the trigger point
  if (hoursElapsed < rule.afterHours) {
    return false
  }

  // Check if this rule has already been triggered
  const alreadyTriggered = previousEscalations.some(
    (event) => event.ruleId === rule.id && event.status === EscalationStatus.COMPLETED
  )

  return !alreadyTriggered
}

/**
 * Get pending escalations for processing
 */
export function getPendingEscalations(
  events: EscalationEvent[]
): EscalationEvent[] {
  return events.filter((event) => event.status === EscalationStatus.PENDING)
}

/**
 * Calculate escalation metrics
 */
export function calculateEscalationMetrics(
  events: EscalationEvent[],
  startDate: number,
  endDate: number
): EscalationMetrics {
  const periodEvents = events.filter(
    (event) => event.createdAt >= startDate && event.createdAt <= endDate
  )

  const byRole: Record<EscalationRole, number> = {
    [EscalationRole.SUPERVISOR]: 0,
    [EscalationRole.DEPARTMENT_HEAD]: 0,
    [EscalationRole.DIRECTOR]: 0,
    [EscalationRole.ADMIN]: 0
  }

  const byAction: Record<EscalationAction, number> = {
    [EscalationAction.NOTIFY]: 0,
    [EscalationAction.REASSIGN]: 0,
    [EscalationAction.ESCALATE]: 0
  }

  const byStatus: Record<EscalationStatus, number> = {
    [EscalationStatus.PENDING]: 0,
    [EscalationStatus.PROCESSING]: 0,
    [EscalationStatus.COMPLETED]: 0,
    [EscalationStatus.FAILED]: 0
  }

  let totalResponseTime = 0
  let processedCount = 0

  periodEvents.forEach((event) => {
    byRole[event.notifyRole]++
    byAction[event.action]++
    byStatus[event.status]++

    if (event.processedAt) {
      totalResponseTime += event.processedAt - event.createdAt
      processedCount++
    }
  })

  const averageResponseTime =
    processedCount > 0 ? totalResponseTime / processedCount / 60000 : 0 // Convert to minutes

  const successRate =
    periodEvents.length > 0
      ? (byStatus[EscalationStatus.COMPLETED] / periodEvents.length) * 100
      : 0

  return {
    period: { start: startDate, end: endDate },
    totalEscalations: periodEvents.length,
    byRole,
    byAction,
    byStatus,
    averageResponseTime,
    successRate
  }
}

/**
 * Format escalation event for display
 */
export function formatEscalationEvent(event: EscalationEvent): string {
  const parts: string[] = []

  parts.push(`Ticket #${event.ticketNumber}`)
  parts.push(`escalated to ${event.notifyRole}`)

  if (event.action === EscalationAction.REASSIGN && event.wasReassigned) {
    parts.push('and reassigned')
  } else if (event.action === EscalationAction.ESCALATE && event.priorityChanged) {
    parts.push(`priority changed to ${event.newPriority}`)
  }

  parts.push(`after ${Math.round(event.hoursElapsed)} hours`)

  return parts.join(' ')
}

/**
 * Generate unique escalation ID
 */
function generateEscalationId(): string {
  return `esc-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}
